/**
 * Login 画面: Google ログインボタンのみ。
 * `<form method="POST" action="/auth/google">` で Cloudflare Turnstile token を
 * 一緒に送る。 Worker が siteverify を通したあと oauth_state を発行して
 * Google へ 302 redirect、 callback 後 Cookie セッション確立 → redirect 先へ復帰。
 *
 * Turnstile は React SPA で auto-render が空振りするので explicit render
 * (window.turnstile.render) で明示的にマウントする。 React 18 strict mode の
 * 二重 mount で重複描画されないよう cleanup で remove する。
 */

import { useEffect, useRef, useState } from 'react';
// 型宣言は lib/turnstile.ts に集約しているのでこちらでは re-declare しない
import '../lib/turnstile';

/** Turnstile site key. dev は test key (always-pass) にフォールバック。
 *  本番は Pages の build env (`VITE_TURNSTILE_SITE_KEY`) で実 key を注入。 */
const TURNSTILE_SITE_KEY: string =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ??
  '1x00000000000000000000AA';

export function Login({ redirect }: { redirect?: string }) {
  const widgetRef = useRef<HTMLDivElement>(null);
  // Turnstile が token を発行するまでログインボタンは disabled。
  // expired/error で false に戻して再 challenge を促す。
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    let widgetId: string | null = null;
    let cancelled = false;
    let timer: number | null = null;
    const tryRender = () => {
      if (cancelled || !widgetRef.current) return;
      if (!window.turnstile) {
        timer = window.setTimeout(tryRender, 100);
        return;
      }
      widgetId = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        callback: () => setTokenReady(true),
        'expired-callback': () => setTokenReady(false),
        'error-callback': () => setTokenReady(false),
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return (
    <main className="centered">
      <div className="card login-card">
        {/* ロゴはトップと同じスラッシュ表記に揃える。 */}
        <h1>
          mixed<span className="home-mark-slash">/</span>games
        </h1>
        <form method="POST" action="/auth/google" className="login-form">
          {redirect && <input type="hidden" name="redirect" value={redirect} />}
          <div ref={widgetRef} className="login-turnstile" />
          <button
            type="submit"
            className="btn-primary login-cta"
            disabled={!tokenReady}
            aria-disabled={!tokenReady}
          >
            <GoogleIcon />
            {tokenReady ? 'Google でログイン' : '認証中…'}
          </button>
        </form>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#fff"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.86 2.69-6.62z"
      />
      <path
        fill="#fff"
        opacity=".85"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.83.86-3.06.86-2.36 0-4.36-1.6-5.07-3.74H.92v2.34A9 9 0 0 0 9 18z"
      />
      <path fill="#fff" opacity=".7" d="M3.93 10.68A5.4 5.4 0 0 1 3.65 9c0-.58.1-1.15.28-1.68V4.98H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.02l3.01-2.34z" />
      <path fill="#fff" opacity=".55" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .92 4.98l3.01 2.34C4.64 5.18 6.64 3.58 9 3.58z" />
    </svg>
  );
}
